/**
 * The Overview chart, as specified in the redesign handoff: area under a 2.25px line, the
 * previous period as a dashed line on the same axis, 5 y ticks at a "nice" max, ~6 date labels,
 * and a hover guide, dot and tooltip. Plain DOM + SVG; values are formatted by the caller.
 * Chart notes, when given, are diamonds on the x-axis; their text shows in the day's tooltip.
 */
import type { ChartNote } from "../src/types";

export interface Series {
  labels: string[]; // one per day
  current: (number | null)[];
  previous?: (number | null)[];
  metric: string;
  format: (n: number) => string;
  /** Formats a y tick (may differ, e.g. durations drop "00s"). */
  tick?: (n: number) => string;
  /**
   * The mobile handoff's chart: 150px tall, two gridlines (50% / 100%) labelled on the right,
   * three date labels, and tap-and-drag scrubbing instead of hover.
   */
  compact?: boolean;
  /** YYYY-MM-DD per label; needed to place `notes`. */
  days?: string[];
  /** Chart notes; ones outside `days` are skipped. */
  notes?: ChartNote[];
  /** Desktop: clicking a day calls this (people who may add notes). */
  onPickDay?: (day: string) => void;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** The handoff's nice max: the smallest step of 1/2/2.5/5/10 × 10^k whose ×4 covers the value. */
export function niceMax(m: number): number {
  const p = Math.pow(10, Math.floor(Math.log10(m || 1)));
  const s = [1, 2, 2.5, 5, 10].find((x) => x * p * 4 >= m) ?? 10;
  return s * p * 4;
}

const NS = "http://www.w3.org/2000/svg";

export function areaChart(host: HTMLElement, s: Series): void {
  const n = s.labels.length;
  const prev = s.previous ?? [];
  const vals = [...s.current, ...prev].filter((v): v is number => v !== null);
  const top = niceMax(Math.max(0, ...vals));
  const H = s.compact ? 150 : 260;
  const X = (i: number) => (n <= 1 ? 500 : (i / (n - 1)) * 1000);
  const Y = (v: number) => H - (v / top) * H;
  const path = (a: (number | null)[]) => {
    let d = "";
    let pen = false;
    a.forEach((v, i) => {
      if (v === null) return void (pen = false);
      d += `${pen ? "L" : "M"}${X(i).toFixed(1)} ${Y(v).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  };

  const ticks = s.compact
    ? [2, 4].map((t) => ({ v: (top * t) / 4, top: `${100 - t * 25}%` }))
    : [0, 1, 2, 3, 4].map((t) => ({ v: (top * t) / 4, top: `${100 - t * 25}%` }));
  const fmtTick = s.tick ?? s.format;
  host.innerHTML = s.compact
    ? `<div class="chart compact"><div class="plot-col">
        <div class="plot">${ticks.map((t) => `<div class="grid" style="top:${t.top}"><span>${fmtTick(t.v)}</span></div>`).join("")}</div>
        <div class="xaxis"></div></div></div>`
    : `<div class="chart">
    <div class="yaxis">${ticks.map((t) => `<span style="top:${t.top}">${fmtTick(t.v)}</span>`).join("")}</div>
    <div class="plot-col">
      <div class="plot">${ticks.map((t) => `<div class="grid" style="top:${t.top}"></div>`).join("")}</div>
      <div class="xaxis"></div>
    </div></div>`;
  const plot = host.querySelector<HTMLElement>(".plot")!;
  const xaxis = host.querySelector<HTMLElement>(".xaxis")!;

  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", `0 0 1000 ${H}`);
  svg.setAttribute("preserveAspectRatio", "none");
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", `${s.metric} per day`);
  const line = path(s.current);
  const first = s.current.findIndex((v) => v !== null);
  let last = -1;
  s.current.forEach((v, i) => v !== null && (last = i));
  svg.innerHTML = `<defs><linearGradient id="mbfill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#2F5BD3" stop-opacity="0.18"/><stop offset="1" stop-color="#2F5BD3" stop-opacity="0"/></linearGradient></defs>
    ${first >= 0 && n > 1 ? `<path d="${line} L${X(last)} ${H} L${X(first)} ${H} Z" fill="url(#mbfill)"/>` : ""}
    ${prev.length ? `<path d="${path(prev)}" fill="none" stroke="#A9B0BF" stroke-width="1.5" stroke-dasharray="4 4" vector-effect="non-scaling-stroke"/>` : ""}
    <path d="${line}" fill="none" stroke="#2F5BD3" stroke-width="2.25" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>`;
  plot.append(svg);
  if (n === 1 && s.current[0] !== null) {
    const d = document.createElement("div");
    d.className = "hdot fixed";
    d.style.left = "50%";
    d.style.top = `${(Y(s.current[0]!) / H) * 100}%`;
    plot.append(d);
  }

  // About six date labels (three on mobile): first left-aligned, last right-aligned, the rest centred.
  let xi: number[] = [];
  if (s.compact) xi = n > 2 ? [0, Math.floor((n - 1) / 2), n - 1] : n === 2 ? [0, 1] : [0];
  else {
    const step = Math.max(1, Math.ceil((n - 1) / 6));
    for (let i = 0; i < n; i += step) xi.push(i);
    if (n > 1 && n - 1 - xi[xi.length - 1]! < step * 0.6) xi.pop();
    if (n > 1) xi.push(n - 1);
  }
  xaxis.innerHTML = xi
    .map((i) => {
      const left = n <= 1 ? 50 : (i / (n - 1)) * 100;
      const shift = n <= 1 ? "translateX(-50%)" : i === 0 ? "none" : i === n - 1 ? "translateX(-100%)" : "translateX(-50%)";
      return `<span style="left:${left}%;transform:${shift}">${s.labels[i]}</span>`;
    })
    .join("");

  // Notes by day index: a dashed line through the plot and a diamond on the x-axis.
  const notesAt = new Map<number, ChartNote[]>();
  if (s.days && s.notes?.length) {
    const idx = new Map(s.days.map((d, i) => [d, i]));
    for (const note of s.notes) {
      const i = idx.get(note.day);
      if (i !== undefined) notesAt.set(i, [...(notesAt.get(i) ?? []), note]);
    }
  }
  const pct = (i: number) => (n <= 1 ? 50 : (i / (n - 1)) * 100);
  for (const [i, list] of notesAt) {
    const cat = `cat-${list[0]!.category}`;
    const nl = document.createElement("div");
    nl.className = `nline ${cat}`;
    nl.style.left = `${pct(i)}%`;
    const mark = document.createElement("button");
    mark.type = "button";
    mark.className = `nmark ${cat}`;
    mark.style.left = `${pct(i)}%`;
    mark.dataset.i = String(i);
    mark.setAttribute("aria-label", `${s.labels[i]}: ${list.map((x) => x.text).join("; ")}`);
    plot.append(nl, mark);
  }

  // Hover: guide, dot and tooltip; the tooltip flips left past 70% of the width.
  const guide = document.createElement("div");
  const dot = document.createElement("div");
  const tip = document.createElement("div");
  guide.className = "guide";
  dot.className = "hdot";
  tip.className = "tip";
  for (const el of [guide, dot, tip]) (el.hidden = true), plot.append(el);
  const show = (i: number) => {
    const v = s.current[i];
    const l = pct(i);
    guide.style.left = `${l}%`;
    tip.style.left = `${l}%`;
    tip.style.transform = l > 70 ? "translateX(calc(-100% - 12px))" : "translateX(12px)";
    const p = prev[i];
    const notes = notesAt.get(i) ?? [];
    tip.innerHTML =
      `<span class="d">${s.labels[i]}${i === n - 1 ? " (today)" : ""}</span>` +
      `<span class="r"><span>${s.metric}</span><b>${v === null || v === undefined ? "–" : s.format(v)}</b></span>` +
      (prev.length ? `<span class="r p"><span>Previous</span><span>${p === null || p === undefined ? "–" : s.format(p)}</span></span>` : "") +
      notes.map((x) => `<span class="nt cat-${x.category}">${esc(x.text)}</span>`).join("") +
      (s.onPickDay && !s.compact && s.days ? `<span class="hint">Click to add a note</span>` : "");
    guide.hidden = tip.hidden = false;
    if (v !== null && v !== undefined) {
      dot.style.left = `${l}%`;
      dot.style.top = `${(Y(v) / H) * 100}%`;
      dot.hidden = false;
    } else dot.hidden = true;
  };
  const dayAt = (clientX: number) => {
    const b = plot.getBoundingClientRect();
    const f = (clientX - b.left) / b.width;
    return n <= 1 ? 0 : Math.max(0, Math.min(n - 1, Math.round(f * (n - 1))));
  };
  // Pointer events cover mouse hover and, on touch, tap-and-drag scrubbing (vertical drags still
  // scroll the page because of touch-action: pan-y in the CSS).
  plot.addEventListener("pointermove", (ev) => show(dayAt(ev.clientX)));
  // Keyboard: a focused note marker shows its day.
  plot.addEventListener("focusin", (ev) => {
    const i = (ev.target as HTMLElement).dataset.i;
    if (i !== undefined) show(Number(i));
  });
  plot.addEventListener("focusout", () => hide());
  if (s.onPickDay && s.days && !s.compact) {
    plot.classList.add("pickable");
    plot.addEventListener("click", (ev) => {
      // Touch taps scrub instead; small screens add notes from the button under the chart.
      const type = (ev as PointerEvent).pointerType;
      if ((type && type !== "mouse") || (ev.target as HTMLElement).closest(".nmark")) return;
      s.onPickDay!(s.days![dayAt(ev.clientX)]!);
    });
  }
  const hide = () => (guide.hidden = dot.hidden = tip.hidden = true);
  plot.addEventListener("pointerleave", hide);
  plot.addEventListener("pointercancel", hide);
  plot.addEventListener("pointerdown", (ev) => {
    if (ev.pointerType !== "mouse") plot.dispatchEvent(new PointerEvent("pointermove", ev));
  });
  plot.addEventListener("pointerup", (ev) => ev.pointerType !== "mouse" && setTimeout(hide, 1500));
}

/** A KPI sparkline: up to 72×24, 1.6px stroke. */
export function sparkline(values: (number | null)[], color: string): string {
  const v = values.map((x) => x ?? 0);
  const n = v.length;
  if (n < 2) return "";
  const mx = Math.max(...v);
  const mn = Math.min(...v);
  const d = v.map((x, i) => `${i ? "L" : "M"}${((i / (n - 1)) * 100).toFixed(1)} ${(26 - ((x - mn) / (mx - mn || 1)) * 24).toFixed(1)}`).join(" ");
  return `<svg viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden="true"><path d="${d}" fill="none" stroke="${color}" stroke-width="1.6" vector-effect="non-scaling-stroke" stroke-linejoin="round"/></svg>`;
}

/** "↑ 12%" / "↓ 4%" pill comparing with the previous period; `invert` for metrics that improve downwards (bounce rate). */
export function deltaPill(cur: number | null | undefined, prev: number | null | undefined, invert = false): string {
  if (cur === null || cur === undefined || prev === null || prev === undefined || prev === 0)
    return `<span class="delta" title="No previous period to compare">–</span>`;
  const pct = ((cur - prev) / prev) * 100;
  const good = invert ? pct < 0 : pct > 0;
  const cls = Math.round(pct) === 0 ? "" : good ? "good" : "bad";
  return `<span class="delta ${cls}">${pct >= 0 ? "↑" : "↓"} ${Math.abs(pct).toFixed(0)}%</span>`;
}
